import React, { useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  Grid,
  Box,
  makeStyles,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import {
  Quotation,
  CoverageCatalog,
  CoverageCatalogResume,
} from 'data-layer-cr/dist/models';
import { MessageBox, DefaultButton } from 'design-system';

// Data Layer
import { assignUid } from 'data-layer-cr/dist/functions/helpers';
import { maskAndSortUnits } from 'data-layer-cr/dist/functions/quotations';
import { COMPLETE_VEHICLE } from 'data-layer-cr/dist/constants/products';
import { LEGAL_PERSON } from 'data-layer-cr/dist/constants/customerTypes';

// Redux
import { RootState } from 'src/store/rootReducer';

// Hooks
import { useCoverageColectiveResume } from 'src/components/screens/hooks';

import CollectiveDesktopDisplay from './CollectiveDesktopDisplay';
import CollectiveMobileDisplay from './CollectiveMobileDisplay';

const useStyle = makeStyles(() => ({
  userName: {
    fontWeight: 800,
    fontSize: 16,
  },
  message: {
    fontWeight: 400,
    fontSize: 16,
  },
}));

export const CollectiveSelection = ({
  quotation,
  maxAllowedPayments,
  coverageCatalogs,
  coverageCatalogResumes,
  onSelected,
}: Props): JSX.Element => {
  const classes = useStyle();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { additionalBenefits, regularCoverageCatalogs } =
    useCoverageColectiveResume({
      quotationCoverages: coverageCatalogs,
    });

  const unit = useMemo(() => {
    return assignUid(
      quotation.productId === COMPLETE_VEHICLE
        ? maskAndSortUnits(quotation)
        : quotation.units,
    )[0].data;
  }, [quotation]);

  const contractingName = useMemo(() => {
    if (quotation.customerType === LEGAL_PERSON) {
      return quotation.legalEntity?.name || '';
    }
    return quotation.customer.firstName;
  }, [quotation]);

  return (
    <Grid container justifyContent="center">
      <Grid container spacing={3} item xs={12} md={12} lg={10} xl={8}>
        <Grid item xs={12}>
          <MessageBox
            data-testid="prompt-message"
            message={
              <>
                <span
                  className={classes.userName}
                  data-testid="customer-name-prompt"
                >
                  {contractingName}
                </span>{' '}
                <span className={classes.message}>
                  , antes de avanzar con la emisión verifica la cotización.
                </span>
              </>
            }
            variant="info"
            hideCloseButton
          />
        </Grid>

        {!isMobile && (
          <CollectiveDesktopDisplay
            data-testid="collective-desktop-display"
            quotation={quotation}
            paymentAmount={maxAllowedPayments}
            unit={unit}
            coverageCatalogs={regularCoverageCatalogs}
            coverageCatalogResumes={coverageCatalogResumes}
            additionalBenefits={additionalBenefits}
          />
        )}

        {isMobile && (
          <CollectiveMobileDisplay
            data-testid="collective-mobile-display"
            quotation={quotation}
            paymentAmount={maxAllowedPayments}
            unit={unit}
            coverageCatalogs={regularCoverageCatalogs}
            coverageCatalogResumes={coverageCatalogResumes}
            additionalBenefits={additionalBenefits}
          />
        )}

        <Grid item container justifyContent="center" xs={12}>
          <Grid item xs={12} md={4}>
            <Box px={4}>
              <DefaultButton
                onClick={(): void => onSelected(unit.package.id, true)}
                fullWidth
                data-testid="next-button"
              >
                siguiente
              </DefaultButton>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  quotation: Quotation;
  coverageCatalogs: CoverageCatalog[];
  coverageCatalogResumes: CoverageCatalogResume[];
  maxAllowedPayments: number;
} => ({
  quotation: state.quotation.quotation,
  coverageCatalogs: state.coverageCatalog.coverageCatalogs,
  coverageCatalogResumes: state.coverageCatalog.coverageCatalogResumes,
  maxAllowedPayments: state.emissionPolicy.paymentFields.paymentQuantity,
});

const connector = connect(mapStateToProps, {});
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onSelected: (packageId: number, forceNext: boolean) => void;
}

export default connector(CollectiveSelection);
